import React, { useEffect, useRef, useState } from "react";
import "./DigitalOMR.css";
import { useHistory, useParams } from "react-router-dom";
import OmrSheetModal from "./OmrSheetModal";
import OmrApi, { omrError } from "./omrApi";
import Notification from "components/Notification";
import Loader from "components/Loader";
import Description from "@material-ui/icons/Description";
import Category from "@material-ui/icons/Category";
import Check from "@material-ui/icons/Check";
import CloudUpload from "@material-ui/icons/CloudUpload";
import AccessTime from "@material-ui/icons/AccessTime";
import AssignmentTurnedIn from "@material-ui/icons/AssignmentTurnedIn";
import AutoFixHigh from "@material-ui/icons/Cached";
import InsertDriveFile from "@material-ui/icons/InsertDriveFile";
import VpnKey from "@material-ui/icons/VpnKey";
import OndemandVideo from "@material-ui/icons/OndemandVideo";
import Replay from "@material-ui/icons/Replay";
import Send from "@material-ui/icons/Send";

const EMPTY = {
  name: "",
  questions: "",
  duration: "",
  subjectId: "",
  positive: "1",
  negative: "0.25",
  youtube: "",
};

// Unwrap a Laravel-style response: { data: <payload> } or the payload itself.
const unwrap = (resp) => {
  const d = resp && resp.data;
  if (d && Object.prototype.hasOwnProperty.call(d, "data")) return d.data;
  return d;
};
const asList = (payload) =>
  Array.isArray(payload) ? payload : (payload && payload.data) || [];

// Normalise an extracted/saved answer key (array of {question_number,
// correct_answer}) OR a plain object into { [q]: "A" }.
const toAnswerMap = (raw) => {
  const map = {};
  if (Array.isArray(raw)) {
    raw.forEach((a) => {
      if (a && a.question_number) map[a.question_number] = a.correct_answer;
    });
  } else if (raw && typeof raw === "object") {
    Object.keys(raw).forEach((k) => (map[k] = raw[k]));
  }
  return map;
};

export default function CreateTestPaper() {
  const history = useHistory();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState(EMPTY);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]); // chapters of selected subject
  const [chapterIds, setChapterIds] = useState([]); // selected chapter ids
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [paperFile, setPaperFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  // In edit mode the paper/key PDFs are already uploaded — keep their URLs so we
  // can show them and treat re-upload as optional.
  const [existingPaper, setExistingPaper] = useState("");
  const [existingKey, setExistingKey] = useState("");
  const [paperCode, setPaperCode] = useState("");
  const [loadingPaper, setLoadingPaper] = useState(isEdit);
  const [answers, setAnswers] = useState({}); // { [q]: "A" }
  const [sheetOpen, setSheetOpen] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [creating, setCreating] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [notif, setNotif] = useState(null);

  const paperRef = useRef(null);
  const keyRef = useRef(null);

  const notify = (type, message) => setNotif({ type, message, id: Date.now() });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const reset = () => {
    setForm(EMPTY);
    setChapters([]);
    setChapterIds([]);
    setPaperFile(null);
    setKeyFile(null);
    setAnswers({});
    setAttempted(false);
  };

  // Load subjects for the subject dropdown.
  useEffect(() => {
    OmrApi.listSubjects({ with_chapters: 0 })
      .then((resp) => setSubjects(asList(unwrap(resp))))
      .catch((err) => notify("error", omrError(err, "Failed to load subjects.")));
  }, []);

  // Load the chapters of a subject into the dependent dropdown. Returns a
  // promise so edit-mode prefill can wait before restoring picked chapters.
  const loadChaptersFor = (subjectId) => {
    if (!subjectId) {
      setChapters([]);
      return Promise.resolve([]);
    }
    setLoadingChapters(true);
    return OmrApi.listSubjectChapters(subjectId)
      .then((resp) => {
        const list = asList(unwrap(resp));
        setChapters(list);
        return list;
      })
      .catch((err) => {
        notify("error", omrError(err, "Failed to load chapters."));
        return [];
      })
      .finally(() => setLoadingChapters(false));
  };

  // When the subject changes, load its chapters (dependent dropdown) and clear
  // any previously-picked chapters.
  const onSubjectChange = (e) => {
    const subjectId = e.target.value;
    setForm((p) => ({ ...p, subjectId }));
    setChapterIds([]);
    loadChaptersFor(subjectId);
  };

  // Edit mode: fetch the paper and prefill the whole form.
  useEffect(() => {
    if (!isEdit) return;
    setLoadingPaper(true);
    OmrApi.getTestPaper(id)
      .then((resp) => {
        const d = unwrap(resp) || {};
        const subjectId =
          d.omr_subject_id || (d.subject && d.subject.id) || "";
        setForm({
          name: d.name || "",
          questions: d.no_of_questions != null ? String(d.no_of_questions) : "",
          duration:
            d.duration_minutes != null ? String(d.duration_minutes) : "",
          subjectId: subjectId ? String(subjectId) : "",
          positive:
            d.positive_marking != null ? String(d.positive_marking) : "1",
          negative:
            d.negative_marking != null ? String(d.negative_marking) : "0.25",
          youtube: d.youtube_url || "",
        });
        setExistingPaper(d.paper_pdf_url || "");
        setExistingKey(d.answer_key_pdf_url || "");
        setPaperCode(d.code || "");
        setAnswers(toAnswerMap(d.answer_keys));
        // restore picked chapters once the subject's chapter list is loaded
        const picked = (d.chapters || []).map((c) =>
          typeof c === "object" ? c.id : c,
        );
        if (subjectId) {
          loadChaptersFor(subjectId).then(() => setChapterIds(picked));
        }
      })
      .catch((err) =>
        notify("error", omrError(err, "Failed to load the test paper.")),
      )
      .finally(() => setLoadingPaper(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleChapter = (id) =>
    setChapterIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const selectAllChapters = () => setChapterIds(chapters.map((c) => c.id));

  const selectedSubjectName = () => {
    const s = subjects.find((x) => String(x.id) === String(form.subjectId));
    return s ? s.name : "";
  };

  const qCount = parseInt(form.questions, 10) || 0;
  const answeredCount = Object.keys(answers).length;

  // first missing required field (also used to flag fields red)
  const errors = {
    name: !form.name.trim(),
    subject: !form.subjectId,
    chapters: chapterIds.length === 0,
    questions: !(parseInt(form.questions, 10) > 0),
    duration: !(parseInt(form.duration, 10) > 0),
    // Re-upload is optional when editing — an existing PDF already covers it.
    paper: !paperFile && !existingPaper,
    key: !keyFile && !existingKey,
  };
  const firstError = () => {
    if (errors.name) return "Paper name is required.";
    if (errors.questions) return "Enter a valid number of questions.";
    if (errors.duration) return "Enter a valid duration (in minutes).";
    if (errors.subject) return "Please select a subject.";
    if (errors.chapters) return "Select at least one chapter.";
    if (errors.paper) return "Please upload the question paper PDF.";
    if (errors.key) return "Please upload the answer key PDF.";
    return null;
  };
  const errCls = (k) => (attempted && errors[k] ? " err" : "");

  // Extract answers from the uploaded answer-key PDF (preview — nothing saved).
  const extract = () => {
    if (!keyFile) {
      notify("error", "Upload the answer key PDF first.");
      return;
    }
    const fd = new FormData();
    fd.append("answer_key_pdf", keyFile);
    if (qCount) fd.append("no_of_questions", String(qCount));
    setExtracting(true);
    OmrApi.previewAnswerKey(fd)
      .then((resp) => {
        const d = (resp.data && resp.data.data) || resp.data || {};
        const map = toAnswerMap(d.extracted_answer_key);
        setAnswers(map);
        const warns = d.warnings || [];
        if (warns.length) {
          notify("warning", Array.isArray(warns) ? warns[0] : String(warns));
        } else {
          notify(
            "success",
            `Extracted ${Object.keys(map).length} answers — verify and save.`,
          );
        }
        setSheetOpen(true);
      })
      .catch((err) => notify("error", omrError(err, "Could not extract the answer key.")))
      .finally(() => setExtracting(false));
  };

  const submit = () => {
    setAttempted(true);
    const msg = firstError();
    if (msg) {
      notify("error", msg);
      return;
    }

    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("omr_subject_id", String(form.subjectId));
    fd.append("chapter_ids", JSON.stringify(chapterIds.map(Number)));
    fd.append("no_of_questions", String(qCount));
    fd.append("duration_minutes", String(parseInt(form.duration, 10)));
    fd.append("positive_marking", String(Math.abs(parseFloat(form.positive) || 0)));
    fd.append("negative_marking", String(Math.abs(parseFloat(form.negative) || 0)));
    fd.append("youtube_url", form.youtube.trim());
    // Only send PDFs that were (re)selected — in edit mode the existing files
    // stay untouched when no new file is chosen.
    if (paperFile) fd.append("paper_pdf", paperFile);
    if (keyFile) fd.append("answer_key_pdf", keyFile);
    // verified answers as JSON array
    const answerArr = Object.keys(answers)
      .map((q) => ({ question_number: Number(q), correct_answer: answers[q] }))
      .sort((a, b) => a.question_number - b.question_number);
    fd.append("answers", JSON.stringify(answerArr));

    setCreating(true);
    const req = isEdit
      ? OmrApi.updateTestPaper(id, fd)
      : OmrApi.createTestPaper(fd);
    req
      .then((resp) => {
        const d = (resp.data && resp.data.data) || {};
        notify(
          "success",
          (resp.data && resp.data.message) ||
            (isEdit
              ? "Test paper updated."
              : `Test paper created${d.code ? ` (${d.code})` : ""}.`),
        );
        setTimeout(() => history.push("/admin/digital-omr"), 1200);
      })
      .catch((err) =>
        notify(
          "error",
          omrError(
            err,
            isEdit
              ? "Failed to update the test paper."
              : "Failed to create the test paper.",
          ),
        ),
      )
      .finally(() => setCreating(false));
  };

  if (loadingPaper) {
    return (
      <div
        className="domr-page"
        style={{ display: "flex", justifyContent: "center", padding: 60 }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <div className="domr-page">
      {notif && (
        <Notification
          key={notif.id}
          type={notif.type}
          title={notif.message}
          duration={3500}
        />
      )}

      <h1 className="domr-page-title">
        {isEdit ? "Edit Test Paper" : "Create New Test Paper"}
      </h1>
      <p className="domr-page-sub">
        {isEdit
          ? "Update the details below and save your changes."
          : "Fill in the details below to generate a new test paper."}
      </p>

      {/* ---- TEST PAPER DETAILS ------------------------------------------ */}
      <div className="domr-card">
        <div className="domr-card-head">
          <span className="domr-chip">
            <Description />
          </span>
          <h3>Test Paper Details</h3>
        </div>

        <div className="domr-grid">
          <div className="domr-field">
            <label>Paper Code</label>
            <div className="domr-generate" style={{ cursor: "default" }}>
              <AutoFixHigh style={{ fontSize: 18 }} />
              {isEdit && paperCode ? paperCode : "Auto-generated on save"}
            </div>
          </div>

          <div className="domr-field">
            <label>
              Paper Name <span className="req">*</span>
            </label>
            <input
              className={"domr-input" + errCls("name")}
              placeholder="Enter paper name"
              value={form.name}
              onChange={set("name")}
            />
          </div>

          <div className="domr-field">
            <label>
              No. of Questions <span className="req">*</span>
            </label>
            <input
              className={"domr-input" + errCls("questions")}
              type="number"
              min="1"
              placeholder="Enter number"
              value={form.questions}
              onChange={set("questions")}
            />
          </div>

          <div className="domr-field">
            <label>
              Duration (Minutes) <span className="req">*</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                className={"domr-input" + errCls("duration")}
                type="number"
                min="1"
                placeholder="Enter duration"
                value={form.duration}
                onChange={set("duration")}
                style={{ paddingRight: 38 }}
              />
              <AccessTime
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#b3b7c7",
                  fontSize: 20,
                }}
              />
            </div>
          </div>

          <div className="domr-field span-2">
            <label>
              Subject <span className="req">*</span>
            </label>
            <div className="domr-select-wrap">
              <Category className="domr-select-ico" />
              <select
                className={"domr-input domr-select" + errCls("subject")}
                value={form.subjectId}
                onChange={onSubjectChange}
              >
                <option value="">
                  {subjects.length
                    ? "Select a subject"
                    : "No subjects — add one first"}
                </option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="domr-field span-2">
            <label>
              Chapters <span className="req">*</span>
            </label>
            <div className="domr-chapter-pick">
              <div className="domr-chapter-pick-head">
                <span className="domr-pick-count">
                  {chapterIds.length
                    ? `${chapterIds.length} selected`
                    : "Select one or more chapters"}
                </span>
                {form.subjectId && chapters.length > 0 && (
                  <span className="domr-pick-controls">
                    <button type="button" onClick={selectAllChapters}>
                      Select all
                    </button>
                    <button type="button" onClick={() => setChapterIds([])}>
                      Clear
                    </button>
                  </span>
                )}
              </div>
              <div
                className={
                  "domr-chips-box" +
                  (attempted && errors.chapters ? " err" : "")
                }
              >
                {!form.subjectId ? (
                  <div className="domr-checklist-empty">
                    Select a subject to choose chapters.
                  </div>
                ) : loadingChapters ? (
                  <div className="domr-checklist-empty">Loading chapters…</div>
                ) : chapters.length === 0 ? (
                  <div className="domr-checklist-empty">
                    No chapters in this subject. Add some on the Chapters page.
                  </div>
                ) : (
                  chapters.map((c) => {
                    const on = chapterIds.includes(c.id);
                    return (
                      <button
                        type="button"
                        key={c.id}
                        className={"domr-chip-toggle" + (on ? " on" : "")}
                        onClick={() => toggleChapter(c.id)}
                      >
                        {on && <Check style={{ fontSize: 15 }} />}
                        {c.name}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="domr-field span-2">
            <label>Positive Marking</label>
            <input
              className="domr-input pos"
              type="number"
              step="0.25"
              value={form.positive}
              onChange={set("positive")}
            />
            <div className="domr-hint">Marks for correct answer</div>
          </div>

          <div className="domr-field span-2">
            <label>Negative Marking</label>
            <input
              className="domr-input neg"
              type="number"
              step="0.25"
              value={form.negative}
              onChange={set("negative")}
            />
            <div className="domr-hint">Marks deducted for wrong answer</div>
          </div>
        </div>

        {/* OMR answer-sheet launcher: extract from PDF, or fill manually */}
        <div className="domr-omr-banner">
          <span className="domr-chip">
            <AssignmentTurnedIn />
          </span>
          <div className="meta">
            <strong>Fill OMR Answer Sheet</strong>
            <span>
              {answeredCount
                ? `${answeredCount}${qCount ? ` / ${qCount}` : ""} answers set`
                : qCount
                ? "Extract from the key PDF or fill manually."
                : "Enter number of questions first."}
            </span>
          </div>
          <button
            type="button"
            className="domr-btn domr-btn-ghost"
            disabled={!keyFile || extracting}
            onClick={extract}
          >
            {extracting ? "Extracting…" : "Extract from PDF"}
          </button>
          <button
            type="button"
            className="domr-btn domr-btn-soft"
            disabled={!qCount}
            onClick={() => setSheetOpen(true)}
          >
            Open Sheet
          </button>
        </div>
      </div>

      {/* ---- UPLOADS ----------------------------------------------------- */}
      <div className="domr-card">
        <div className="domr-card-head">
          <span className="domr-chip">
            <CloudUpload />
          </span>
          <h3>Uploads</h3>
        </div>

        <div className="domr-uploads">
          <div className="domr-upload">
            <h4>
              Paper Upload <span className="req">*</span>
            </h4>
            <div className="domr-drop" style={attempted && errors.paper ? { outline: "1px solid #ef4444" } : undefined}>
              <div className="domr-drop-ico">
                <InsertDriveFile />
              </div>
              <p>
                {paperFile
                  ? paperFile.name
                  : existingPaper
                  ? "Current paper uploaded — pick a file to replace it"
                  : "Upload question paper"}
              </p>
              <small>PDF only, max 10MB</small>
              <button
                type="button"
                className="domr-upload-btn"
                onClick={() => paperRef.current && paperRef.current.click()}
              >
                Upload PDF
              </button>
              <input
                ref={paperRef}
                type="file"
                accept="application/pdf"
                hidden
                onChange={(e) => setPaperFile(e.target.files[0] || null)}
              />
            </div>
          </div>

          <div className="domr-upload">
            <h4>
              Answer Key Upload <span className="req">*</span>
            </h4>
            <div className="domr-drop green" style={attempted && errors.key ? { outline: "1px solid #ef4444" } : undefined}>
              <div className="domr-drop-ico">
                <VpnKey />
              </div>
              <p>
                {keyFile
                  ? keyFile.name
                  : existingKey
                  ? "Current key uploaded — pick a file to replace it"
                  : "Upload answer key"}
              </p>
              <small>PDF only, max 10MB</small>
              <button
                type="button"
                className="domr-upload-btn green"
                onClick={() => keyRef.current && keyRef.current.click()}
              >
                Upload PDF
              </button>
              <input
                ref={keyRef}
                type="file"
                accept="application/pdf"
                hidden
                onChange={(e) => setKeyFile(e.target.files[0] || null)}
              />
            </div>
          </div>

          <div className="domr-upload">
            <h4>
              YouTube Video <span className="opt">(Optional)</span>
            </h4>
            <div className="domr-drop">
              <div className="domr-drop-ico red">
                <OndemandVideo />
              </div>
              <p>Add explanatory video link</p>
              <input
                className="domr-input"
                placeholder="Enter YouTube URL"
                value={form.youtube}
                onChange={set("youtube")}
                style={{ marginTop: 6 }}
              />
              <div className="domr-hint" style={{ textAlign: "center" }}>
                Example: https://youtube.com/watch?v=…
              </div>
            </div>
          </div>
        </div>

        <div className="domr-form-actions" style={{ marginTop: 24 }}>
          <button
            type="button"
            className="domr-btn domr-btn-ghost"
            onClick={reset}
            disabled={creating}
          >
            <Replay style={{ fontSize: 18 }} /> Reset
          </button>
          <button
            type="button"
            className="domr-btn domr-btn-primary"
            onClick={submit}
            disabled={creating}
          >
            <Send style={{ fontSize: 18 }} />
            {isEdit
              ? creating
                ? "Saving…"
                : "Save Changes"
              : creating
              ? "Creating…"
              : "Create Test Paper"}
          </button>
        </div>
      </div>

      <OmrSheetModal
        open={sheetOpen}
        title={`${selectedSubjectName() || "Test"} - OMR Sheet`}
        count={qCount}
        initialAnswers={answers}
        onClose={() => setSheetOpen(false)}
        onSave={(a) => {
          setAnswers(a);
          setSheetOpen(false);
          notify("success", `${Object.keys(a).length} answers saved.`);
        }}
      />
    </div>
  );
}
